import { FormikProvider, useFormik } from 'formik';
import { ChangeEvent, useCallback } from 'react';
import * as yup from 'yup';
import { AiTrackerEmailBlock } from '@/modules/issuer-dashboard/components/portfolios/AITrackerEmailBlock.tsx';
import {
  useIssuerAddPortfolioBrokerMutation,
  useIssuerUpdatePortfolioBrokerMutation,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { BaseModalProps, Button, ModalContentWrapper, TextInputField } from 'ui';
import { BrokerExtended, CreateBrokerRequest } from 'api';

const validationSchema = yup.object<CreateBrokerRequest>({
  id: yup.number().nullable().optional(),
  name: yup.string().trim().required('This field is required').max(40, 'Maximum length 40'),
});

export interface ModalBrokerAddUpdateProps extends BaseModalProps {
  item?: BrokerExtended;
  portfolioId: number;
}

export const ModalBrokerAddUpdate = ({
  open,
  onClose,
  item,
  portfolioId,
}: ModalBrokerAddUpdateProps) => {
  const formik = useFormik<CreateBrokerRequest>({
    validationSchema,
    initialValues: {
      name: item?.name || '',
      portfolio_id: portfolioId,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: values => {
      if (item) {
        mutateAsyncUpdate({
          ...values,
          broker_id: item.id,
        });
      } else {
        mutateAsyncAdd(values);
      }
    },
  });

  const { mutateAsync: mutateAsyncAdd, isLoading: isLoadingAdd } =
    useIssuerAddPortfolioBrokerMutation({
      setErrors: formik.setErrors,
      onSuccess: onClose,
    });
  const { mutateAsync: mutateAsyncUpdate, isLoading: isLoadingUpdate } =
    useIssuerUpdatePortfolioBrokerMutation({
      setErrors: formik.setErrors,
      onSuccess: onClose,
    });

  const handleChangeSelect = useCallback(
    (field: string) => (option: string | boolean | null | any) => {
      formik.setFieldValue(field, option);
      formik.setFieldError(field, '');
    },
    [formik],
  );

  const handleChangeInputBank = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const name = e.target.name;
      const value = e.target.value;

      handleChangeSelect(name)(value);
    },
    [handleChangeSelect],
  );

  return (
    <ModalContentWrapper
      onClose={onClose}
      open={open}
      title={item ? 'Update broker' : 'New broker'}
      classes={{ children: 'p-0' }}
    >
      <FormikProvider value={formik}>
        <div className="p-3 sm:p-6 flex flex-col w-full gap-6">
          <TextInputField
            name="name"
            label="Broker name"
            onChange={handleChangeInputBank}
            value={formik.values.name}
            error={formik.errors.name}
          />
          <AiTrackerEmailBlock />
        </div>
        <div className="mt-4 p-3 !pb-0 sm:p-6">
          <Button
            type="submit"
            onClick={formik.handleSubmit}
            loading={isLoadingAdd || isLoadingUpdate}
          >
            {item ? 'Update broker information' : 'Add new broker'}
          </Button>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
